<template>
  <div
    class="fixed overflow-auto py-4 top-0 w-full pointer-events-none z-[2147483645]"
  >
    <TransitionGroup name="fade" tag="div" class="space-y-1 px-2">
      <div
        v-for="notification in notificationsInApp"
        :key="notification.id"
        class="flex justify-center"
      >
        <button
          class="shadow rounded-lg px-4.5 flex items-center gap-2 min-h-[54px] wrapper-animation overflow-hidden pointer-events-auto group"
          :class="[NOTIFICATIONS_STYLES[notification.type].wrapper]"
          @click="handleClose(notification)"
        >
          <!-- START -->
          <FontAwesomeIcon
            class="!flex icon-animation"
            :class="[
              ICON_CLASS,
              { 'group-hover:!hidden': !!notification.route },
            ]"
            :icon="['fas', NOTIFICATIONS_STYLES[notification.type].icon]"
          />
          <FontAwesomeIcon
            v-if="!!notification.route"
            class="!hidden group-hover:!flex"
            :class="[
              ICON_CLASS,
              NOTIFICATIONS_STYLES[notification.type].button,
            ]"
            :icon="['fas', 'eye']"
          />
          <!-- BODY -->
          <div class="flex flex-col justify-center text-animation flex-grow">
            <div v-if="notification.title">{{ $t(notification.title) }}</div>
            <div class="text-sm">{{ $t(notification.text) }}</div>
          </div>
          <!-- END -->
          <FontAwesomeIcon
            v-if="notification.closeOnClick"
            role="button"
            class="!flex opacity-0 group-hover:opacity-100 transition-opacity"
            :class="[
              ICON_CLASS,
              NOTIFICATIONS_STYLES[notification.type].button,
            ]"
            :icon="['fas', 'xmark']"
          />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import useNotification from '../../composables/useNotification';
import type { NotificationInApp } from '../../composables/useNotification';
const configStore = useConfigStore();
const { notificationsInApp, position } = storeToRefs(configStore);
const { closeNotification, resetNotifications } = useNotification();

const ICON_CLASS =
  'flex-none rounded-full bg-white items-center justify-center h-5.5 w-5.5 text-sm';

const NOTIFICATIONS_STYLES = {
  info: {
    icon: 'bell',
    wrapper: 'text-blue bg-blue-10',
    button: 'hover:bg-blue hover:text-white transition-colors',
  },
  error: {
    icon: 'exclamation-triangle',
    wrapper: 'text-red bg-red-10',
    button: 'hover:bg-red hover:text-white transition-colors',
  },
  danger: {
    icon: 'exclamation-triangle',
    wrapper: 'text-black bg-yellow-1 border-2 border-yellow',
    button: 'hover:bg-yellow hover:text-white transition-colors',
  },
  success: {
    icon: 'check',
    wrapper: 'text-green bg-green-10',
    button: 'hover:bg-green hover:text-white transition-colors',
  },
};

onMounted(() => {
  resetNotifications();
});

const router = useRouter();

const handleClose = (notification: NotificationInApp) => {
  closeNotification(notification.id);
  if (notification.route) {
    router.push(notification.route);
  }
};
</script>

<style scoped>
.wrapper-animation {
  width: 54px;
  height: 54px;
  animation-name: expand;
  animation-duration: 250ms;
  animation-fill-mode: forwards;
  animation-delay: 750ms;
  transition-timing-function: ease-in-out;
}

@keyframes expand {
  0% {
    width: 54px;
    height: 54px;
  }
  100% {
    width: 100%;
    height: auto;
  }
}

.text-animation {
  opacity: 0;
  animation-name: fade;
  animation-duration: 250ms;
  animation-fill-mode: forwards;
  animation-delay: 750ms;
  transition-timing-function: ease-in-out;
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.icon-animation {
  transform: rotateZ(0deg);
  animation-name: rotate;
  animation-duration: 500ms;
  animation-fill-mode: forwards;
  animation-delay: 1000ms;
  transform-origin: center center;
  transition-timing-function: ease-in-out;
}

@keyframes rotate {
  0% {
    transform: rotateZ(0deg);
  }
  50% {
    transform: rotateZ(-30deg);
  }
  100% {
    transform: rotateZ(0deg);
  }
}
</style>
